import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Headphones,
  FileText,
  Mic,
  CheckCircle2,
  Loader2,
  ImageIcon,
  Calendar,
  Lock,
  Plus,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { authFetch } from '../utils/authUtils';
import CoverImagePicker from '../components/CoverImagePicker';
import OriginalContentAck from '../components/OriginalContentAck';
import { recordOriginalAck } from '../utils/originalAck';
import { toast } from 'sonner';
import VoiceSelectionModal from '../components/VoiceSelectionModal';
import SynthesisProgressLine from '../components/SynthesisProgressLine';
import { pollChapterSynthesis } from '../utils/pollChapterSynthesis';
import { validateAudioDuration } from '../utils/probeAudioDuration';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GENRES = [
  'Fiction', 'Non-Fiction', 'Science Fiction', 'Fantasy', 'Mystery',
  'Thriller', 'Romance', 'Poetry', 'Biography', 'History', 'Science',
  'Self-Help', 'Business', 'Technology', 'Education', 'Other',
];

const LEVELS = [
  { id: 'basic', label: 'Basic', desc: 'Easy reads' },
  { id: 'intermediate', label: 'Intermediate', desc: 'Moderate' },
  { id: 'advanced', label: 'Advanced', desc: 'Challenging' },
];

const TEXT_MIN = 1000;
const TEXT_MAX = 6000;

/** Top-level 2-step wizard for audiobook series + chapter authoring. */
const UploadAudiobookSeries = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState(null);

  // Step state: 1 = series setup, 2 = episode editor.
  const [step, setStep] = useState(1);

  // --- Step 1 (series) ---
  const [seriesId, setSeriesId] = useState(null);
  const [coverUrl, setCoverUrl] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [genre, setGenre] = useState('');
  const [creatingSeries, setCreatingSeries] = useState(false);

  // --- Step 2 (episode) ---
  const [chapterNumber, setChapterNumber] = useState(1);
  const [chapterTitle, setChapterTitle] = useState('');
  const [readingLevel, setReadingLevel] = useState(''); // empty until writer picks
  const [bodyMode, setBodyMode] = useState('text'); // 'text' | 'audio'
  const [chapterText, setChapterText] = useState('');
  const [audioFile, setAudioFile] = useState(null);
  const [episodeCover, setEpisodeCover] = useState(null);
  const [releaseAt, setReleaseAt] = useState('');
  const [uploadingChapter, setUploadingChapter] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  // Series-level voice lock — see VoiceSelectionModal usage below.
  const [lockedVoice, setLockedVoice] = useState(null);
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  // Live progress for background-task synthesis.
  const [synthProgress, setSynthProgress] = useState(null);

  // Hydrate the lock state whenever seriesId becomes available — covers
  // the "create series then immediately upload episode 1" path so the
  // pill renders the moment the series is born without an action.
  useEffect(() => {
    if (!seriesId) { setLockedVoice(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/audiobook/series/${seriesId}`);
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setLockedVoice(d?.tts_voice || null);
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [seriesId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/auth/me`);
        if (res.ok && !cancelled) {
          const data = await res.json();
          setRole(data.role || null);
        }
      } catch {/* noop */}
    })();
    return () => { cancelled = true; };
  }, []);

  const wordCount = useMemo(() => {
    const trimmed = chapterText.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter(Boolean).length;
  }, [chapterText]);

  const wordCountColor = wordCount === 0
    ? 'text-slate-500'
    : wordCount < TEXT_MIN
    ? 'text-amber-400'
    : wordCount > TEXT_MAX
    ? 'text-rose-400'
    : 'text-emerald-400';

  // --- Step 1 submit -------------------------------------------------
  const canCreateSeries = title.trim() && author.trim() && genre.trim();

  // --- Originality acknowledgements -----------------------------------
  // One ack at series-create time (covers the series concept), then one
  // PER chapter at chapter-save time (each chapter is its own work).
  const [seriesOriginalAck, setSeriesOriginalAck] = useState(false);
  const [chapterOriginalAck, setChapterOriginalAck] = useState(false);

  const handleCreateSeries = async () => {
    if (!canCreateSeries) {
      toast.error('Fill Series Title, Author, and Genre to continue.');
      return;
    }
    if (!seriesOriginalAck) {
      toast.error('Please confirm the originality acknowledgement first.');
      return;
    }
    setCreatingSeries(true);
    try {
      const res = await authFetch(`${BACKEND_URL}/api/audiobook/series/create`, {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          author: author.trim(),
          genre,
          cover_image_url: coverUrl || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to create series.');
        return;
      }
      setSeriesId(data.series_id);
      setStep(2);
      toast.success('Series created. Add your first episode.');
      if (data.series_id) {
        recordOriginalAck(data.series_id).catch(() => {});
      }
    } catch (e) {
      toast.error('Network error creating series.');
    } finally {
      setCreatingSeries(false);
    }
  };

  // --- Step 2 submit -------------------------------------------------
  const canSubmitChapter = (() => {
    if (!chapterTitle.trim()) return false;
    if (!readingLevel) return false;
    if (bodyMode === 'text') return wordCount >= TEXT_MIN && wordCount <= TEXT_MAX;
    return !!audioFile;
  })();

  // Inline explainer mirrored from the cinematic page — kills the
  // "I clicked Publish and nothing happened" bug by surfacing exactly
  // what's still missing under the disabled button.
  const chapterBlockReason = (() => {
    if (uploadingChapter) return null;
    if (!chapterTitle.trim()) return 'Add a chapter title.';
    if (!readingLevel) return 'Pick a reading level.';
    if (bodyMode === 'text') {
      if (wordCount < TEXT_MIN) return `Script is too short — write at least ${TEXT_MIN.toLocaleString()} words (you have ${wordCount.toLocaleString()}).`;
      if (wordCount > TEXT_MAX) return `Script is too long — keep it under ${TEXT_MAX.toLocaleString()} words (you have ${wordCount.toLocaleString()}).`;
    } else if (!audioFile) {
      return 'Attach an audio file (MP3 or WAV) to publish — or switch to “Narrate with my TTS voice”.';
    }
    if (!chapterOriginalAck) return 'Tick the originality acknowledgement at the bottom of the form.';
    return null;
  })();

  const handleSubmitChapter = async (chosenVoice = null) => {
    if (!canSubmitChapter) {
      toast.error(chapterBlockReason || 'Complete the chapter form before saving.');
      return;
    }
    if (!chapterOriginalAck) {
      toast.error('Please confirm the originality acknowledgement first.');
      return;
    }
    // Voice lock — text-mode + no series voice locked yet + caller
    // hasn't passed a voice → open the picker. Modal calls back into
    // handleSubmitChapter(voice) on confirm.
    if (bodyMode === 'text' && !lockedVoice && !chosenVoice) {
      setVoiceModalOpen(true);
      return;
    }
    setUploadingChapter(true);
    try {
      const fd = new FormData();
      fd.append('chapter_number', String(chapterNumber));
      fd.append('chapter_title', chapterTitle.trim());
      fd.append('reading_level', readingLevel);
      if (bodyMode === 'text') {
        fd.append('chapter_text', chapterText.trim());
        fd.append('tts_voice', lockedVoice || chosenVoice || 'default');
      } else if (audioFile) {
        fd.append('file', audioFile);
      }
      if (episodeCover instanceof File) {
        fd.append('cover_image', episodeCover);
      }
      if (releaseAt && role === 'admin') {
        fd.append('release_at', releaseAt);
      }

      const res = await authFetch(
        `${BACKEND_URL}/api/audiobook/series/${seriesId}/chapter`,
        { method: 'POST', body: fd },
      );
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Failed to save episode.');
        return;
      }
      // First-time lock — update the local state so the next episode
      // shows the locked-voice pill without a refetch.
      if (bodyMode === 'text' && !lockedVoice && chosenVoice) {
        setLockedVoice(chosenVoice);
      }
      // Background-task synthesis — poll for audio if pending.
      const newId = data?.chapter_id || data?.episode_id || data?.book_id;
      if (data?.synthesis_status === 'pending' && newId) {
        toast.success(`Chapter ${chapterNumber} submitted — narrating audio…`);
        setSynthProgress({ elapsedMs: 0, attempt: 0, status: 'pending' });
        pollChapterSynthesis({ kind: 'audiobook', id: newId, onProgress: setSynthProgress })
          .then(() => {
            toast.success(`Chapter ${chapterNumber} audio is live.`);
            setSynthProgress(null);
          })
          .catch((err) => {
            toast.error(`Audio synthesis failed: ${err.message || err}`);
            setSynthProgress(null);
          });
      } else {
        toast.success(`Chapter ${chapterNumber} published.`);
      }
      // Stay on editor, reset, increment.
      setLastSaved({ number: chapterNumber, title: chapterTitle.trim() });
      if (newId) recordOriginalAck(newId).catch(() => {});
      setChapterNumber((n) => n + 1);
      setChapterTitle('');
      setReadingLevel('');
      setBodyMode('text');
      setChapterText('');
      setAudioFile(null);
      setEpisodeCover(null);
      setReleaseAt('');
      setChapterOriginalAck(false);
      toast.success(`Episode ${chapterNumber} saved.`);
    } catch (e) {
      toast.error('Network error saving episode.');
    } finally {
      setUploadingChapter(false);
    }
  };

  // -------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="upload-audiobook-series-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/upload')}
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            data-testid="upload-audiobook-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Headphones className="w-4 h-4 text-fuchsia-400" />
            <span>Audiobook Series</span>
          </div>
        </div>

        {/* Step indicator */}
        <div className="mb-8 flex items-center gap-3" data-testid="upload-audiobook-stepper">
          <StepBadge n={1} active={step === 1} done={step > 1} label="Series Setup" />
          <div className={`h-px flex-1 ${step > 1 ? 'bg-fuchsia-500' : 'bg-slate-800'}`} />
          <StepBadge n={2} active={step === 2} done={false} label="Episode Editor" />
        </div>

        {step === 1 ? (
          <SeriesSetupPanel
            coverUrl={coverUrl}
            setCoverUrl={setCoverUrl}
            title={title}
            setTitle={setTitle}
            description={description}
            setDescription={setDescription}
            author={author}
            setAuthor={setAuthor}
            genre={genre}
            setGenre={setGenre}
            creating={creatingSeries}
            canContinue={Boolean(canCreateSeries) && seriesOriginalAck}
            onContinue={handleCreateSeries}
            originalAck={seriesOriginalAck}
            setOriginalAck={setSeriesOriginalAck}
          />
        ) : (
          <EpisodeEditorPanel
            kind="audio"
            seriesTitle={title}
            chapterNumber={chapterNumber}
            setChapterNumber={setChapterNumber}
            chapterTitle={chapterTitle}
            setChapterTitle={setChapterTitle}
            readingLevel={readingLevel}
            setReadingLevel={setReadingLevel}
            bodyMode={bodyMode}
            setBodyMode={setBodyMode}
            chapterText={chapterText}
            setChapterText={setChapterText}
            wordCount={wordCount}
            wordCountColor={wordCountColor}
            audioFile={audioFile}
            setAudioFile={setAudioFile}
            episodeCover={episodeCover}
            setEpisodeCover={setEpisodeCover}
            releaseAt={releaseAt}
            setReleaseAt={setReleaseAt}
            isAdmin={role === 'admin'}
            canSubmit={canSubmitChapter && chapterOriginalAck}
            submitBlockReason={chapterBlockReason}
            uploading={uploadingChapter}
            onSubmit={() => handleSubmitChapter()}
            lastSaved={lastSaved}
            onFinish={() => navigate('/library')}
            originalAck={chapterOriginalAck}
            setOriginalAck={setChapterOriginalAck}
            lockedVoice={lockedVoice}
            synthProgress={synthProgress}
          />
        )}
      </div>
      <VoiceSelectionModal
        open={voiceModalOpen}
        onCancel={() => setVoiceModalOpen(false)}
        onConfirm={(v) => handleSubmitChapter(v)}
        busy={uploadingChapter}
      />
    </div>
  );
};

// ----- Small subcomponents (kept in-file to keep page self-contained) -----

const StepBadge = ({ n, active, done, label }) => (
  <div className="flex items-center gap-2" data-testid={`step-badge-${n}`}>
    <div
      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border ${
        done
          ? 'bg-fuchsia-500/20 border-fuchsia-500 text-fuchsia-200'
          : active
          ? 'bg-fuchsia-500 border-fuchsia-500 text-white'
          : 'bg-slate-900 border-slate-700 text-slate-500'
      }`}
    >
      {done ? <CheckCircle2 className="w-4 h-4" /> : n}
    </div>
    <span className={`text-xs font-medium ${active ? 'text-white' : 'text-slate-500'}`}>
      {label}
    </span>
  </div>
);

export const SeriesSetupPanel = ({
  coverUrl, setCoverUrl,
  title, setTitle,
  description, setDescription,
  author, setAuthor,
  genre, setGenre,
  creating, canContinue, onContinue,
  originalAck, setOriginalAck,
}) => (
  <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-6 space-y-5" data-testid="series-setup-panel">
    <header>
      <h2 className="text-lg font-semibold">Step 1 — Series Setup</h2>
      <p className="text-xs text-slate-500 mt-1">
        Cover image, title, description, author, and genre. You'll add episodes next.
      </p>
    </header>

    <CoverImagePicker
      value={coverUrl}
      onChange={setCoverUrl}
      label="Cover image"
    />

    <div>
      <label className="text-sm font-medium text-slate-200 mb-1 block">Series title</label>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. The Night Wanderer"
        className="bg-slate-950 border-slate-700 text-white placeholder-slate-600"
        data-testid="series-title-input"
      />
    </div>

    <div>
      <label className="text-sm font-medium text-slate-200 mb-1 block">Description</label>
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What's this series about?"
        rows={3}
        className="bg-slate-950 border-slate-700 text-white placeholder-slate-600"
        data-testid="series-description-input"
      />
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="text-sm font-medium text-slate-200 mb-1 block">Author</label>
        <Input
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="Your pen name"
          className="bg-slate-950 border-slate-700 text-white placeholder-slate-600"
          data-testid="series-author-input"
        />
      </div>
      <div>
        <label className="text-sm font-medium text-slate-200 mb-1 block">Genre</label>
        <select
          value={genre}
          onChange={(e) => setGenre(e.target.value)}
          className="w-full bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-2 text-sm"
          data-testid="series-genre-select"
        >
          <option value="">Select genre…</option>
          {GENRES.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
      </div>
    </div>

    <OriginalContentAck
      checked={originalAck}
      onChange={setOriginalAck}
      testId="series-setup-original-ack"
    />

    <Button
      onClick={onContinue}
      disabled={!canContinue || creating}
      className="w-full bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-semibold"
      data-testid="series-continue-btn"
    >
      {creating ? (
        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
      ) : (
        <>Continue to episodes <ArrowRight className="w-4 h-4 ml-2" /></>
      )}
    </Button>
  </div>
);

export const EpisodeEditorPanel = ({
  kind, // 'audio' | 'cinematic' — only affects copy.
  seriesTitle,
  chapterNumber, setChapterNumber,
  chapterTitle, setChapterTitle,
  readingLevel, setReadingLevel,
  bodyMode, setBodyMode,
  chapterText, setChapterText,
  wordCount, wordCountColor,
  audioFile, setAudioFile,
  episodeCover, setEpisodeCover,
  releaseAt, setReleaseAt,
  isAdmin,
  canSubmit, uploading,
  onSubmit, onFinish, lastSaved,
  originalAck, setOriginalAck,
  lockedVoice,
  synthProgress,
  submitBlockReason, // human-readable explainer when canSubmit=false
}) => {
  const noun = kind === 'cinematic' ? 'Episode' : 'Episode';
  // When the publish button is disabled the user previously saw NOTHING
  // on click — no toast, no inline hint, no visual cue. That created
  // "I clicked Publish and nothing happened" reports. We now make the
  // button itself ALWAYS clickable so the upstream onSubmit can show
  // the appropriate toast, AND surface a yellow hint row beneath
  // explaining exactly what's still missing.
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-5 sm:p-6 space-y-5" data-testid="episode-editor-panel">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Step 2 — {noun} Editor</h2>
          <p className="text-xs text-slate-500 mt-1">
            Adding to <span className="text-fuchsia-300">{seriesTitle || 'your series'}</span>. Save and we'll bump the {noun.toLowerCase()} number for the next one.
          </p>
        </div>
        {lastSaved && (
          <div className="text-[11px] text-emerald-400 flex items-center gap-1" data-testid="last-saved-indicator">
            <CheckCircle2 className="w-3.5 h-3.5" /> #{lastSaved.number} saved
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr] gap-3">
        <div>
          <label className="text-sm font-medium text-slate-200 mb-1 block">{noun} #</label>
          <Input
            type="number"
            min={1}
            value={chapterNumber}
            onChange={(e) => setChapterNumber(parseInt(e.target.value || '1', 10))}
            className="bg-slate-950 border-slate-700 text-white"
            data-testid="episode-number-input"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-slate-200 mb-1 block">{noun} title</label>
          <Input
            value={chapterTitle}
            onChange={(e) => setChapterTitle(e.target.value)}
            placeholder={`e.g. ${noun} ${chapterNumber} — The Discovery`}
            className="bg-slate-950 border-slate-700 text-white placeholder-slate-600"
            data-testid="episode-title-input"
          />
        </div>
      </div>

      {/* Reading level */}
      <div>
        <label className="text-sm font-medium text-slate-200 mb-1 block">
          Reading level <span className="text-xs text-rose-400">required</span>
        </label>
        <div className="grid grid-cols-3 gap-2">
          {LEVELS.map((lvl) => {
            const active = readingLevel === lvl.id;
            return (
              <button
                key={lvl.id}
                type="button"
                onClick={() => setReadingLevel(active ? '' : lvl.id)}
                data-testid={`level-${lvl.id}`}
                className={`rounded-lg border px-3 py-2 text-left transition-all ${
                  active
                    ? 'border-fuchsia-500 bg-fuchsia-500/10 text-fuchsia-100'
                    : 'border-slate-700 bg-slate-950/60 text-slate-300 hover:border-fuchsia-500/40'
                }`}
              >
                <div className="font-semibold text-sm">{lvl.label}</div>
                <div className="text-[10px] opacity-70">{lvl.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Body switcher — visible side-by-side choice between TTS
          narration and audio upload. The TTS card briefly explains the
          voice lock so writers know what they're committing to. */}
      <div>
        <label className="text-sm font-medium text-slate-200 mb-2 block">
          How will this {noun.toLowerCase()} be narrated?
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3" data-testid="body-mode-cards">
          <button
            type="button"
            onClick={() => setBodyMode('text')}
            data-testid="body-mode-text"
            className={`group rounded-xl border p-3 text-left transition ${
              bodyMode === 'text'
                ? 'border-fuchsia-400 bg-gradient-to-br from-fuchsia-500/15 to-purple-500/10 shadow-[0_0_0_1px_rgba(217,70,239,0.35)]'
                : 'border-slate-700 bg-slate-950/60 hover:border-fuchsia-400/60'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <Mic className={`w-4 h-4 ${bodyMode === 'text' ? 'text-fuchsia-300' : 'text-slate-400'}`} />
              <span className="font-bold text-sm text-white">Narrate with my TTS voice</span>
            </div>
            <p className="text-[11px] text-slate-400 leading-snug">
              Paste your script (1,000–6,000 words). We&apos;ll convert it
              to audio with your locked voice and upload it for you.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setBodyMode('audio')}
            data-testid="body-mode-audio"
            className={`group rounded-xl border p-3 text-left transition ${
              bodyMode === 'audio'
                ? 'border-emerald-400 bg-gradient-to-br from-emerald-500/15 to-emerald-500/5 shadow-[0_0_0_1px_rgba(52,211,153,0.4)]'
                : 'border-slate-700 bg-slate-950/60 hover:border-emerald-400/60'
            }`}
          >
            <div className="flex items-center gap-2 mb-1">
              <FileText className={`w-4 h-4 ${bodyMode === 'audio' ? 'text-emerald-300' : 'text-slate-400'}`} />
              <span className="font-bold text-sm text-white">Upload my own audio file</span>
            </div>
            <p className="text-[11px] text-slate-400 leading-snug">
              Drop an MP3 or WAV recording — we&apos;ll publish it as-is.
              No voice lock applies to audio uploads.
            </p>
          </button>
        </div>

        {bodyMode === 'text' ? (
          <div>
            <Textarea
              value={chapterText}
              onChange={(e) => setChapterText(e.target.value)}
              placeholder="Write your story here…"
              rows={12}
              className="bg-slate-950 border-slate-700 text-white placeholder-slate-600 font-mono text-sm"
              data-testid="episode-text-input"
            />
            <div className={`mt-2 text-xs ${wordCountColor}`} data-testid="word-count">
              {wordCount} words · need {TEXT_MIN}–{TEXT_MAX}
            </div>
          </div>
        ) : (
          <AudioFileDropzone file={audioFile} onChange={setAudioFile} />
        )}
      </div>

      {/* Optional writer options */}
      <details className="rounded-lg border border-slate-800 bg-slate-950/40" data-testid="optional-writer-options">
        <summary className="cursor-pointer text-sm text-slate-300 px-3 py-2 select-none">
          <span className="font-medium">Optional</span> — per-episode cover{isAdmin ? ', scheduled release' : ''}
        </summary>
        <div className="px-3 pb-3 pt-1 space-y-4">
          <div>
            <label className="text-xs font-medium text-slate-400 mb-1 block flex items-center gap-1">
              <ImageIcon className="w-3.5 h-3.5" /> Per-episode cover (overrides series cover)
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setEpisodeCover(e.target.files?.[0] || null)}
              className="block w-full text-xs text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-xs file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700"
              data-testid="episode-cover-input"
            />
            {episodeCover && (
              <div className="text-[11px] text-emerald-400 mt-1">
                Selected: {episodeCover.name}
                <button
                  type="button"
                  onClick={() => setEpisodeCover(null)}
                  className="ml-2 text-rose-400 hover:underline"
                >Remove</button>
              </div>
            )}
          </div>

          {isAdmin && (
            <div>
              <label className="text-xs font-medium text-slate-400 mb-1 block flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Scheduled release <Lock className="w-3 h-3 text-slate-500" />
                <span className="text-[10px] text-slate-500">(admin)</span>
              </label>
              <input
                type="datetime-local"
                value={releaseAt}
                onChange={(e) => setReleaseAt(e.target.value)}
                className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 py-1.5 text-xs w-full"
                data-testid="episode-release-input"
              />
            </div>
          )}
        </div>
      </details>

      {/* Submit + finish */}
      <OriginalContentAck
        checked={originalAck}
        onChange={setOriginalAck}
        testId="episode-editor-original-ack"
      />
      {/* Series-level voice lock pill — only shown for text-mode and
          once a voice has been bound to this series. Tells the writer
          the narrator is fixed for every future text episode. */}
      {bodyMode === 'text' && lockedVoice && (
        <div
          className="rounded-lg border border-fuchsia-500/40 bg-fuchsia-500/10 px-3 py-2 flex items-center gap-2 text-[12px] text-fuchsia-100"
          data-testid="series-voice-locked"
        >
          <Lock className="w-3.5 h-3.5 text-fuchsia-300" />
          <span>
            Narration voice locked for this series:&nbsp;
            <strong className="text-white">{lockedVoice}</strong>
          </span>
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2 pt-2">
        <Button
          onClick={onSubmit}
          disabled={uploading}
          className={`flex-1 font-semibold ${
            canSubmit
              ? 'bg-fuchsia-500 hover:bg-fuchsia-400 text-white'
              : 'bg-fuchsia-500/30 hover:bg-fuchsia-500/40 text-fuchsia-50/80 ring-1 ring-fuchsia-300/40'
          }`}
          data-testid="episode-submit-btn"
          title={submitBlockReason || undefined}
        >
          {uploading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing…</>
          ) : (
            <><Plus className="w-4 h-4 mr-2" /> Publish {noun.toLowerCase()}</>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={onFinish}
          disabled={uploading}
          className="flex-1 border-slate-700 text-slate-200 hover:bg-slate-800"
          data-testid="episode-finish-btn"
        >
          Finish
        </Button>
      </div>
      {/* Inline disabled-reason — shows EXACTLY what's blocking publish
          so the user never sees "I clicked and nothing happened" again.
          Hidden when canSubmit is true. */}
      {!canSubmit && submitBlockReason && (
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
          data-testid="episode-submit-block-reason"
        >
          <span className="text-amber-300 font-bold mt-px">!</span>
          <span>{submitBlockReason}</span>
        </div>
      )}

      <SynthesisProgressLine progress={synthProgress} />

      <p className="text-[11px] text-slate-500 text-center">
        Auto-narration is paused. Text episodes publish without an audio file —
        readers can use the in-app TTS player.
      </p>
    </div>
  );
};

const AudioFileDropzone = ({ file, onChange }) => (
  <label
    className="flex flex-col items-center justify-center w-full p-6 rounded-xl border-2 border-dashed border-slate-700 hover:border-fuchsia-500 bg-slate-950/40 cursor-pointer"
    data-testid="audio-file-dropzone"
  >
    <Mic className="w-6 h-6 text-fuchsia-300 mb-2" />
    <p className="text-sm text-slate-200">
      {file ? file.name : 'Click to choose audio file'}
    </p>
    <p className="text-[11px] text-slate-500 mt-1">
      MP3 · WAV · M4A · AAC · OGG · FLAC · 5–22 minutes
    </p>
    <input
      type="file"
      accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
      onChange={async (e) => {
        const f = e.target.files?.[0] || null;
        if (!f) {
          onChange(null);
          return;
        }
        // Iter 79: client-side probe for 5-22 min window.
        const check = await validateAudioDuration(f);
        if (!check.ok) {
          toast.error(check.message);
          e.target.value = '';
          return;
        }
        onChange(f);
      }}
      className="hidden"
      data-testid="audio-file-input"
    />
    {file && (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onChange(null);
        }}
        className="mt-2 text-[11px] text-rose-400 hover:underline"
      >
        Remove
      </button>
    )}
  </label>
);

export default UploadAudiobookSeries;
